import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { FileText, Palette, BarChart3, ArrowUpDown, Calendar } from 'lucide-react';
import { PLAN_TRANSLATIONS } from '@/constants/planTranslations';
import { format } from 'date-fns';

interface TimelineReportSettings {
  pageFormat: 'portrait' | 'landscape';
  showCategoryColors: boolean;
  showCategories: boolean;
  title: string;
  titlePrefix?: string;
  showLogo?: boolean;
  logoType?: 'solid' | 'enp';
  sortOrder: 'current' | 'category';
  showMilestones?: boolean;
  customDateRange?: {
    enabled: boolean;
    startDate: string;
    endDate: string;
  };
  stretchToFullWidth?: boolean;
  showAssessmentIssues?: boolean;
  showProjectHealth?: boolean;
  showCategoryPerformance?: boolean;
  showStatistics?: boolean;
}

interface TimelineReportSettingsProps {
  settings: TimelineReportSettings;
  onSettingsChange: (settings: TimelineReportSettings) => void;
}

const TimelineReportSettings = ({
  settings,
  onSettingsChange
}: TimelineReportSettingsProps) => {
  const updateSetting = <K extends keyof TimelineReportSettings>(
    key: K,
    value: TimelineReportSettings[K]
  ) => {
    onSettingsChange({
      ...settings,
      [key]: value
    });
  };

  const setQuarterRange = (quarter: 1 | 2 | 3 | 4) => {
    const year = new Date().getFullYear();
    const quarterStart = {
      1: `${year}-01-01`,
      2: `${year}-04-01`,
      3: `${year}-07-01`,
      4: `${year}-10-01`,
    }[quarter];
    const quarterEnd = {
      1: `${year}-03-31`,
      2: `${year}-06-30`,
      3: `${year}-09-30`,
      4: `${year}-12-31`,
    }[quarter];
    updateSetting('customDateRange', {
      enabled: true,
      startDate: quarterStart,
      endDate: quarterEnd
    });
  };

  const setThisMonth = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    updateSetting('customDateRange', {
      enabled: true,
      startDate: format(start, 'yyyy-MM-dd'),
      endDate: format(end, 'yyyy-MM-dd')
    });
  };

  const isValidRange = () => {
    if (!settings.customDateRange?.enabled) return true;
    if (!settings.customDateRange?.startDate || !settings.customDateRange?.endDate) return false;
    return new Date(settings.customDateRange.startDate) <= new Date(settings.customDateRange.endDate);
  };

  return (
    <div className="space-y-4">
      <h3 className="font-medium text-sm text-muted-foreground">{PLAN_TRANSLATIONS.reportSettings}</h3>
      
      <div className="grid grid-cols-1 gap-4">
        {/* Title Prefix */}
        <div className="space-y-2">
          <Label htmlFor="titlePrefix" className="text-sm font-medium">
            Tiền tố tiêu đề
          </Label>
          <Input
            id="titlePrefix"
            value={settings.titlePrefix || 'Project Timeline'}
            onChange={(e) => updateSetting('titlePrefix', e.target.value)}
            placeholder="Project Timeline"
          />
        </div>

        {/* Show Logo */}
        <div className="flex items-center justify-between">
          <Label htmlFor="showLogo" className="text-sm font-medium">
            Hiển thị Logo
          </Label>
          <Switch
            id="showLogo"
            checked={settings.showLogo ?? true}
            onCheckedChange={(checked) => updateSetting('showLogo', checked)}
          />
        </div>

        {/* Logo Type */}
        {(settings.showLogo ?? true) && (
          <div className="space-y-2">
            <Label htmlFor="logoType" className="text-sm font-medium">
              Loại Logo
            </Label>
            <Select
              value={settings.logoType || 'solid'}
              onValueChange={(value: 'solid' | 'enp') => updateSetting('logoType', value)}
            >
              <SelectTrigger id="logoType">
                <SelectValue placeholder="Chọn logo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="solid">Solid</SelectItem>
                <SelectItem value="enp">ENP</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Sort Order */}
        <div className="space-y-2">
          <Label className="text-sm font-medium flex items-center gap-2">
            <ArrowUpDown className="h-4 w-4" />
            {PLAN_TRANSLATIONS.sortOrder}
          </Label>
          <Select value={settings.sortOrder} onValueChange={(value: 'current' | 'category') => updateSetting('sortOrder', value)} disabled>
            <SelectTrigger className="opacity-60">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="current">
                {PLAN_TRANSLATIONS.sortByCurrent}
              </SelectItem>
              <SelectItem value="category">
                {PLAN_TRANSLATIONS.sortByCategory}
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground italic">
            Luôn sử dụng thứ tự hiện tại của timeline
          </p>
        </div>

        {/* Page Format */}
        <div className="space-y-2">
          <Label className="text-sm font-medium flex items-center gap-2">
            <FileText className="h-4 w-4" />
            {PLAN_TRANSLATIONS.pageFormat}
          </Label>
          <Select value={settings.pageFormat} onValueChange={(value: 'portrait' | 'landscape') => updateSetting('pageFormat', value)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="landscape">
                {PLAN_TRANSLATIONS.landscape}
              </SelectItem>
              <SelectItem value="portrait">
                {PLAN_TRANSLATIONS.portrait}
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {settings.pageFormat === 'landscape' 
              ? PLAN_TRANSLATIONS.landscapeDesc
              : PLAN_TRANSLATIONS.portraitDesc
            }
          </p>
        </div>

        {/* Settings toggles */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label htmlFor="showColors" className="text-sm font-medium flex items-center gap-2">
              <Palette className="h-4 w-4" />
              {PLAN_TRANSLATIONS.showCategoryColors}
            </Label>
            <Switch
              id="showColors"
              checked={settings.showCategoryColors}
              onCheckedChange={(checked) => updateSetting('showCategoryColors', checked)}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="showCategories" className="text-sm font-medium flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              {PLAN_TRANSLATIONS.showCategoryColumn}
            </Label>
            <Switch
              id="showCategories"
              checked={settings.showCategories}
              onCheckedChange={(checked) => updateSetting('showCategories', checked)}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <Label htmlFor="showMilestones" className="text-sm font-medium flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Hiển thị Milestones
            </Label>
            <Switch
              id="showMilestones"
              checked={settings.showMilestones !== false}
              onCheckedChange={(checked) => updateSetting('showMilestones', checked)}
            />
          </div>
          
          <div className="flex items-center justify-between pt-2 border-t opacity-60">
            <div className="flex-1">
              <Label htmlFor="stretchToFullWidth" className="text-sm font-medium">
                {PLAN_TRANSLATIONS.stretchToFullWidth}
              </Label>
              <p className="text-xs text-muted-foreground mt-1 italic">
                Luôn bật để tối ưu sử dụng không gian giấy
              </p>
            </div>
            <Switch
              id="stretchToFullWidth"
              checked={settings.stretchToFullWidth || false}
              onCheckedChange={(checked) => updateSetting('stretchToFullWidth', checked)}
              disabled
            />
          </div>
          
          <div className="flex items-center justify-between">
            <Label htmlFor="showProjectHealth" className="text-sm">
              Bao gồm Đánh giá tổng thể dự án
            </Label>
            <Switch
              id="showProjectHealth"
              checked={settings.showProjectHealth !== false}
              onCheckedChange={(checked) => updateSetting('showProjectHealth', checked)}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <Label htmlFor="showCategoryPerformance" className="text-sm">
              Bao gồm Phân tích Categories
            </Label>
            <Switch
              id="showCategoryPerformance"
              checked={settings.showCategoryPerformance !== false}
              onCheckedChange={(checked) => updateSetting('showCategoryPerformance', checked)}
            />
          </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="showStatistics" className="text-sm">
            Hiển thị Thống kê tổng quan (cards)
          </Label>
          <Switch
            id="showStatistics"
            checked={settings.showStatistics === true}
            onCheckedChange={(checked) => updateSetting('showStatistics', checked)}
          />
        </div>
          
          <div className="flex items-center justify-between">
            <Label htmlFor="showAssessmentIssues" className="text-sm">
              Hiển thị Issues & Khuyến nghị
            </Label>
            <Switch
              id="showAssessmentIssues"
              checked={settings.showAssessmentIssues !== false}
              onCheckedChange={(checked) => updateSetting('showAssessmentIssues', checked)}
            />
          </div>
        </div>

        {/* Date Range Section */}
        <div className="space-y-3 pt-3 border-t">
          <Label className="text-sm font-medium flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            {PLAN_TRANSLATIONS.dateRange}
          </Label>
          
          <div className="flex items-center justify-between">
            <Label htmlFor="useCustomRange" className="text-sm">
              {PLAN_TRANSLATIONS.useCustomRange}
            </Label>
            <Switch
              id="useCustomRange"
              checked={settings.customDateRange?.enabled || false}
              onCheckedChange={(checked) => updateSetting('customDateRange', {
                enabled: checked,
                startDate: settings.customDateRange?.startDate || '',
                endDate: settings.customDateRange?.endDate || ''
              })}
            />
          </div>

          {settings.customDateRange?.enabled && (
            <>
              <div className="grid grid-cols-2 gap-3 pl-6">
                <div className="space-y-1.5">
                  <Label className="text-xs">{PLAN_TRANSLATIONS.fromDate}</Label>
                  <Input 
                    type="date"
                    value={settings.customDateRange?.startDate || ''}
                    onChange={(e) => updateSetting('customDateRange', {
                      enabled: true,
                      startDate: e.target.value,
                      endDate: settings.customDateRange?.endDate || ''
                    })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{PLAN_TRANSLATIONS.toDate}</Label>
                  <Input 
                    type="date"
                    value={settings.customDateRange?.endDate || ''}
                    onChange={(e) => updateSetting('customDateRange', {
                      enabled: true,
                      startDate: settings.customDateRange?.startDate || '',
                      endDate: e.target.value
                    })}
                  />
                </div>
              </div>

              {!isValidRange() && (
                <div className="text-xs text-destructive pl-6">
                  ⚠️ {PLAN_TRANSLATIONS.invalidDateRange}
                </div>
              )}

              <div className="flex flex-wrap gap-2 pl-6">
                <Button variant="outline" size="sm" onClick={() => setQuarterRange(1)}>
                  Q1
                </Button>
                <Button variant="outline" size="sm" onClick={() => setQuarterRange(2)}>
                  Q2
                </Button>
                <Button variant="outline" size="sm" onClick={() => setQuarterRange(3)}>
                  Q3
                </Button>
                <Button variant="outline" size="sm" onClick={() => setQuarterRange(4)}>
                  Q4
                </Button>
                <Button variant="outline" size="sm" onClick={() => setThisMonth()}>
                  {PLAN_TRANSLATIONS.thisMonth}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimelineReportSettings;